import {
  ChartBarIcon,
  DocumentIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { Bird } from 'lucide-react';
import { useState } from 'react';

import { DemandExamples } from '../demand-example/DemandExamples';
import { DemandEvaluation } from '../evaluation/DemandEvaluation';
import { GoldenDemands } from '../golden-demands/GoldenDemands';
import { Heading } from '../ui/catalyst/heading';

type ActiveSection = 'examples' | 'golden' | 'evaluation';

export const InternalPage = () => {
  const [activeSection, setActiveSection] = useState<ActiveSection>('examples');
  const [isFlying, setIsFlying] = useState(false);
  const [flightPattern, setFlightPattern] = useState(0);

  const createEmojiRain = () => {
    const emojis = ['✨', '🌟', '⭐', '💫', '🔥', '🌈', '🎉', '🦋', '🍃'];

    for (let i = 0; i < 15; i += 1) {
      setTimeout(() => {
        const emoji = document.createElement('div');
        emoji.innerHTML = emojis[Math.floor(Math.random() * emojis.length)];
        emoji.style.cssText = `
          position: fixed;
          top: -20px;
          left: ${Math.random() * window.innerWidth}px;
          font-size: ${Math.random() * 20 + 15}px;
          z-index: 10000;
          pointer-events: none;
          animation: fall ${Math.random() * 2 + 2}s linear forwards;
        `;

        document.body.appendChild(emoji);

        setTimeout(() => {
          document.body.removeChild(emoji);
        }, 4000);
      }, i * 200);
    }
  };

  const handleBirdHover = () => {
    if (isFlying) return; // Prevent multiple flights

    setIsFlying(true);
    setFlightPattern(Math.floor(Math.random() * 3)); // Random flight pattern

    // Create falling emoji particles
    createEmojiRain();

    // Reset after animation
    setTimeout(() => {
      setIsFlying(false);
    }, 3000);
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'examples':
        return <DemandExamples />;
      case 'golden':
        return <GoldenDemands />;
      case 'evaluation':
        return <DemandEvaluation />;
      default:
        return <DemandExamples />;
    }
  };

  return (
    <div
      className={`flex h-screen bg-gray-50 ${isFlying ? 'animate-pulse' : ''}`}
    >
      {/* INSANE Custom CSS for flying animation */}
      <style>{`
        @keyframes fly-pattern-0 {
          0% { transform: translate(0, 0) rotate(0deg) scale(1.1); }
          15% { transform: translate(100px, 50px) rotate(45deg) scale(1.4); }
          30% { transform: translate(250px, 80px) rotate(90deg) scale(1.2); }
          45% { transform: translate(400px, 150px) rotate(135deg) scale(1.5); }
          60% { transform: translate(500px, 250px) rotate(180deg) scale(1.3); }
          75% { transform: translate(350px, 350px) rotate(225deg) scale(1.4); }
          90% { transform: translate(150px, 200px) rotate(315deg) scale(1.2); }
          100% { transform: translate(0, 0) rotate(720deg) scale(1.1); }
        }
        
        @keyframes fly-pattern-1 {
          0% { transform: translate(0, 0) rotate(0deg) scale(1.1); }
          15% { transform: translate(150px, 80px) rotate(30deg) scale(1.6); }
          30% { transform: translate(300px, 120px) rotate(90deg) scale(1.3); }
          45% { transform: translate(450px, 200px) rotate(150deg) scale(1.7); }
          60% { transform: translate(550px, 300px) rotate(200deg) scale(1.2); }
          75% { transform: translate(400px, 400px) rotate(270deg) scale(1.5); }
          90% { transform: translate(200px, 250px) rotate(330deg) scale(1.3); }
          100% { transform: translate(0, 0) rotate(540deg) scale(1.1); }
        }
        
        @keyframes fly-pattern-2 {
          0% { transform: translate(0, 0) rotate(0deg) scale(1.1); }
          12% { transform: translate(80px, 60px) rotate(20deg) scale(1.5); }
          25% { transform: translate(200px, 100px) rotate(60deg) scale(1.8); }
          37% { transform: translate(350px, 180px) rotate(120deg) scale(1.4); }
          50% { transform: translate(500px, 280px) rotate(180deg) scale(1.6); }
          62% { transform: translate(600px, 350px) rotate(220deg) scale(1.3); }
          75% { transform: translate(450px, 420px) rotate(280deg) scale(1.5); }
          87% { transform: translate(250px, 300px) rotate(320deg) scale(1.2); }
          100% { transform: translate(0, 0) rotate(900deg) scale(1.1); }
        }
        
        @keyframes particle-trail {
          0% { 
            opacity: 1; 
            transform: scale(1) rotate(0deg);
            background: linear-gradient(45deg, #ff6b6b, #4ecdc4, #45b7d1, #96ceb4, #ffeaa7);
          }
          100% { 
            opacity: 0; 
            transform: scale(0.3) rotate(360deg);
            background: linear-gradient(45deg, #fd79a8, #fdcb6e, #6c5ce7, #a29bfe, #fd79a8);
          }
        }
        
        @keyframes screen-shake {
          0%, 100% { transform: translateX(0); }
          10% { transform: translateX(-2px); }
          20% { transform: translateX(2px); }
          30% { transform: translateX(-4px); }
          40% { transform: translateX(4px); }
          50% { transform: translateX(-2px); }
          60% { transform: translateX(2px); }
          70% { transform: translateX(-1px); }
          80% { transform: translateX(1px); }
          90% { transform: translateX(-1px); }
        }
        
        @keyframes fall {
          to {
            transform: translateY(100vh) rotate(360deg);
            opacity: 0;
          }
        }
        
        @keyframes rainbow-glow {
          0% { background: linear-gradient(90deg, #ff6b6b, #4ecdc4); }
          25% { background: linear-gradient(90deg, #4ecdc4, #45b7d1); }
          50% { background: linear-gradient(90deg, #45b7d1, #96ceb4); }
          75% { background: linear-gradient(90deg, #96ceb4, #ffeaa7); }
          100% { background: linear-gradient(90deg, #ffeaa7, #ff6b6b); }
        }
        
        .fly-on-hover.flying-0 {
          animation: fly-pattern-0 3s cubic-bezier(0.68, -0.55, 0.265, 1.55) forwards;
          z-index: 9999;
        }
        
        .fly-on-hover.flying-1 {
          animation: fly-pattern-1 3s cubic-bezier(0.68, -0.55, 0.265, 1.55) forwards;
          z-index: 9999;
        }
        
        .fly-on-hover.flying-2 {
          animation: fly-pattern-2 3s cubic-bezier(0.68, -0.55, 0.265, 1.55) forwards;
          z-index: 9999;
        }
        
        .screen-shake {
          animation: screen-shake 0.5s ease-in-out infinite;
        }
        
        .rainbow-header {
          animation: rainbow-glow 1s ease-in-out infinite;
        }
        
        .particle {
          position: absolute;
          width: 8px;
          height: 8px;
          border-radius: 50%;
          animation: particle-trail 1s ease-out forwards;
          z-index: 9998;
        }
      `}</style>

      {/* Sidebar */}
      <div className="w-64 bg-white border-r border-gray-200 shadow-sm">
        {/* Sidebar Header */}
        <div
          className={`p-6 border-b border-gray-200 flex items-center gap-2 transition-all duration-300 ${isFlying ? 'rainbow-header screen-shake' : ''}`}
        >
          {/* ULTIMATE Flying Bird Icon */}
          <div
            className={`relative group fly-on-hover ${isFlying ? `flying-${flightPattern}` : ''}`}
            onMouseEnter={handleBirdHover}
          >
            {/* Mega Glow effect background */}
            <div
              className={`absolute inset-0 bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 rounded-full blur-sm opacity-0 group-hover:opacity-100 transition-all duration-500 ${isFlying ? 'opacity-100 animate-ping' : ''}`}
            />

            {/* Outer rainbow ring when flying */}
            {isFlying && (
              <div className="absolute -inset-4 bg-gradient-to-r from-red-500 via-yellow-500 via-green-500 via-blue-500 via-indigo-500 to-purple-500 rounded-full animate-spin opacity-75" />
            )}

            {/* Main bird container */}
            <div
              className={`relative bg-gradient-to-br from-gray-600 to-gray-700 group-hover:from-blue-500 group-hover:via-purple-600 group-hover:to-pink-500 rounded-full p-2 transition-all duration-500 shadow-sm group-hover:shadow-xl ${isFlying ? 'shadow-2xl' : ''}`}
            >
              <Bird
                className={`h-6 w-6 text-white drop-shadow-sm group-hover:drop-shadow-lg transition-all duration-500 ${isFlying ? 'drop-shadow-2xl' : ''}`}
                strokeWidth={2}
              />
            </div>

            {/* Epic sparkle effects */}
            <div
              className={`absolute -top-1 -right-1 w-3 h-3 bg-yellow-300 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-ping transition-opacity duration-300 delay-200 ${isFlying ? 'opacity-100' : ''}`}
            />
            <div
              className={`absolute -bottom-1 -left-1 w-2 h-2 bg-pink-300 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-ping transition-opacity duration-300 delay-500 ${isFlying ? 'opacity-100' : ''}`}
            />
            <div
              className={`absolute top-0 left-0 w-2 h-2 bg-blue-300 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-ping transition-opacity duration-300 delay-300 ${isFlying ? 'opacity-100' : ''}`}
            />
          </div>

          <Heading level={1}>The Flock</Heading>
        </div>

        {/* Sidebar Body */}
        <div className="p-4">
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => setActiveSection('examples')}
              className={`w-full flex items-center gap-3 px-3 py-2 text-left text-sm font-medium rounded-md transition-colors ${
                activeSection === 'examples'
                  ? 'bg-blue-50 text-blue-700 border border-blue-200'
                  : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <DocumentIcon className="h-5 w-5" />
              <span>Example Demands</span>
            </button>

            <button
              type="button"
              onClick={() => setActiveSection('golden')}
              className={`w-full flex items-center gap-3 px-3 py-2 text-left text-sm font-medium rounded-md transition-colors ${
                activeSection === 'golden'
                  ? 'bg-blue-50 text-blue-700 border border-blue-200'
                  : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <SparklesIcon className="h-5 w-5" />
              <span>Gold Standards</span>
            </button>

            <button
              type="button"
              onClick={() => setActiveSection('evaluation')}
              className={`w-full flex items-center gap-3 px-3 py-2 text-left text-sm font-medium rounded-md transition-colors ${
                activeSection === 'evaluation'
                  ? 'bg-blue-50 text-blue-700 border border-blue-200'
                  : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <ChartBarIcon className="h-5 w-5" />
              <span>Evaluation</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Content */}
        <div className="flex-1 overflow-auto p-6">{renderContent()}</div>
      </div>
    </div>
  );
};
